import React, { useState, useEffect } from 'react';
import { User, RegisterData } from '../types';
import { authApi } from '../api';
import { useAuth } from '../AuthContext';
import { Navigate } from 'react-router-dom';
import { theme } from '../theme';

const AdminUsers: React.FC = () => {
    const { isAdmin, isOwner, user: currentUser } = useAuth();
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            setIsLoading(true);
            const data = await authApi.getAllUsers();
            setUsers(data);
        } catch (err) {
            setError('Kunne ikke laste brukere');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRoleChange = async (userId: string, newRole: string) => {
        try {
            await authApi.updateUserRole(userId, newRole);
            setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
        } catch (err) {
            setError('Kunne ikke oppdatere rolle');
        }
    };

    const handleUnlockUser = async (userId: string) => {
        try {
            await authApi.unlockUser(userId);
            setUsers(users.map(u => u.id === userId ? { ...u, isLockedOut: false, lockoutEnd: null } : u));
        } catch (err) {
            setError('Kunne ikke låse opp bruker');
        }
    };

    const handleLockUser = async (userId: string) => {
        if (!window.confirm('Er du sikker på at du vil låse denne brukeren?')) return;
        try {
            await authApi.lockUser(userId);
            setUsers(users.map(u => u.id === userId ? { ...u, isLockedOut: true } : u));
        } catch (err) {
            setError('Kunne ikke låse bruker');
        }
    };

    const handleDeleteUser = async (userId: string) => {
        if (!window.confirm('Er du sikker på at du vil SLETTE denne brukeren permanent?')) return;
        try {
            await authApi.deleteUser(userId);
            setUsers(users.filter(u => u.id !== userId));
        } catch (err) {
            setError('Kunne ikke slette bruker');
        }
    };

    const handleUserCreated = (newUser: User) => {
        setUsers([...users, newUser]);
        setShowCreateForm(false);
    };

    if (!isAdmin) {
        return <Navigate to="/" replace />;
    }

    if (isLoading) {
        return <div style={styles.loading}>Laster...</div>;
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Administrasjon</h1>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>Brukere ({users.length})</h2>
                    <button
                        style={styles.createButton}
                        onClick={() => setShowCreateForm(true)}
                    >
                        + Ny beboer
                    </button>
                </div>

                {showCreateForm && (
                    <CreateUserForm
                        onClose={() => setShowCreateForm(false)}
                        onCreated={handleUserCreated}
                    />
                )}

                <div style={styles.userList}>
                    {users.map(user => (
                        <div key={user.id} style={{
                            ...styles.userRow,
                            backgroundColor: user.isLockedOut ? '#fff3e0' : undefined,
                            borderLeft: user.isLockedOut ? '4px solid #ff9800' : undefined
                        }}>
                            <div style={styles.userCell}>
                                <strong>{user.fullName}</strong>
                                {user.isLockedOut && (
                                    <span style={styles.lockedBadge}>🔒 Låst</span>
                                )}
                            </div>
                            <div style={styles.userCell}>{user.email}</div>
                            <div style={styles.userCell}>
                                Leilighet: {user.apartmentNumber || '-'}
                            </div>
                            <div style={styles.userCell}>
                                {isOwner ? (
                                    <select
                                        value={user.role}
                                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                        style={styles.roleSelect}
                                    >
                                        <option value="Beboer">Beboer</option>
                                        <option value="Admin">Styret</option>
                                        <option value="Owner">Admin</option>
                                    </select>
                                ) : (
                                    <span style={styles.roleText}>
                                        {user.role === 'Owner' ? 'Admin' : user.role === 'Admin' ? 'Styret' : 'Beboer'}
                                    </span>
                                )}
                            </div>
                            {user.isLockedOut && (
                                <div style={styles.userCell}>
                                    <button 
                                        style={styles.unlockButton}
                                        onClick={() => handleUnlockUser(user.id)}
                                    >
                                        🔓 Lås opp
                                    </button>
                                </div>
                            )}
                            {isOwner && currentUser?.id !== user.id && (
                                <div style={styles.userCell}>
                                    <div style={styles.ownerActions}>
                                        {!user.isLockedOut && (
                                            <button 
                                                style={styles.lockButton}
                                                onClick={() => handleLockUser(user.id)}
                                                title="Lås bruker"
                                            >
                                                🔒 Lås
                                            </button>
                                        )}
                                        <button 
                                            style={styles.deleteButton}
                                            onClick={() => handleDeleteUser(user.id)}
                                            title="Slett bruker"
                                        >
                                            🗑️ Slett
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

interface CreateUserFormProps {
    onClose: () => void;
    onCreated: (user: User) => void;
}

const CreateUserForm: React.FC<CreateUserFormProps> = ({ onClose, onCreated }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [apartmentNumber, setApartmentNumber] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const generatePassword = () => {
        const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = '';
        for (let i = 0; i < 8; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setPassword(result);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const data: RegisterData = {
                email,
                password,
                fullName,
                apartmentNumber
            };
            await authApi.register(data);
            
            // Fetch all users to get the new user with ID
            const users = await authApi.getAllUsers();
            const newUser = users.find(u => u.email === email);
            if (newUser) {
                onCreated(newUser);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Kunne ikke opprette bruker');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={styles.modal}>
            <div style={styles.modalContent}>
                <div style={styles.modalHeader}>
                    <h2>Opprett ny beboer</h2>
                    <button style={styles.closeButton} onClick={onClose}>✕</button>
                </div>

                {error && <div style={styles.error}>{error}</div>}

                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Fullt navn *</label>
                        <input
                            type="text"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            style={styles.input}
                            placeholder="Ola Nordmann"
                            required
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>E-post *</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            style={styles.input}
                            placeholder="ola@example.com"
                            required
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Leilighet/Hus-nummer</label>
                        <input
                            type="text"
                            value={apartmentNumber}
                            onChange={(e) => setApartmentNumber(e.target.value)}
                            style={styles.input}
                            placeholder="f.eks. 12B"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Passord *</label>
                        <div style={styles.passwordGroup}>
                            <input
                                type="text"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                style={styles.passwordInput}
                                placeholder="Minimum 6 tegn"
                                required
                                minLength={6}
                            />
                            <button
                                type="button"
                                style={styles.generateButton}
                                onClick={generatePassword}
                            >
                                Generer
                            </button>
                        </div>
                        <small style={styles.helpText}>
                            Del passordet med beboeren. De kan endre det senere.
                        </small>
                    </div>

                    <div style={styles.formActions}>
                        <button type="button" style={styles.cancelButton} onClick={onClose}>
                            Avbryt
                        </button>
                        <button type="submit" style={styles.submitButton} disabled={isLoading}>
                            {isLoading ? 'Oppretter...' : 'Opprett bruker'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '16px',
    },
    header: {
        marginBottom: '20px',
    },
    title: {
        margin: 0,
        color: '#333',
        fontSize: '24px',
    },
    loading: {
        textAlign: 'center',
        padding: '48px',
        color: '#666',
    },
    error: {
        padding: '12px',
        backgroundColor: '#ffebee',
        color: '#c62828',
        borderRadius: '4px',
        marginBottom: '16px',
    },
    section: {
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        flexWrap: 'wrap',
        gap: '12px',
    },
    sectionTitle: {
        margin: 0,
        fontSize: '16px',
        color: '#333',
    },
    createButton: {
        padding: '10px 16px',
        backgroundColor: theme.primaryColor,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
    },
    userList: {
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        overflow: 'hidden',
    },
    userHeader: {
        display: 'none',
    },
    userHeaderCell: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    userRow: {
        display: 'flex',
        flexDirection: 'column',
        padding: '12px',
        borderTop: '1px solid #e0e0e0',
        gap: '8px',
    },
    userCell: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: '14px',
    },
    roleSelect: {
        padding: '8px 10px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '14px',
        width: '100%',
        maxWidth: '200px',
    },
    lockedBadge: {
        display: 'inline-block',
        marginLeft: '8px',
        padding: '2px 8px',
        backgroundColor: '#ff9800',
        color: 'white',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'normal',
    },
    unlockButton: {
        padding: '6px 12px',
        backgroundColor: '#ff9800',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '13px',
    },
    modal: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
    },
    modalContent: {
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '16px',
        width: '100%',
        maxWidth: '450px',
        maxHeight: '90vh',
        overflow: 'auto',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '20px',
        cursor: 'pointer',
        color: '#666',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    label: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#333',
    },
    input: {
        padding: '10px 12px',
        fontSize: '16px',
        border: '1px solid #ddd',
        borderRadius: '4px',
    },
    passwordGroup: {
        display: 'flex',
        gap: '8px',
    },
    passwordInput: {
        flex: 1,
        padding: '10px 12px',
        fontSize: '16px',
        border: '1px solid #ddd',
        borderRadius: '4px',
    },
    generateButton: {
        padding: '10px 16px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #ddd',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
    },
    helpText: {
        color: '#666',
        fontSize: '12px',
        marginTop: '4px',
    },
    formActions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginTop: '8px',
    },
    cancelButton: {
        padding: '10px 20px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #ddd',
        borderRadius: '4px',
        cursor: 'pointer',
    },
    submitButton: {
        padding: '10px 20px',
        backgroundColor: theme.primaryColor,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 'bold',
    },
    roleText: {
        padding: '8px 0',
        fontSize: '14px',
        color: '#333',
    },
    ownerActions: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
    },
    lockButton: {
        padding: '6px 12px',
        backgroundColor: '#ff9800',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '13px',
    },
    deleteButton: {
        padding: '6px 12px',
        backgroundColor: '#f44336',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '13px',
    },
};

export default AdminUsers;
